import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Modal, Select, Switch } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useAdmin from "../../hooks/useAdmin";
import useData from "../../datas/useData";
import { serverErrorMessage } from "../../lib/serverError";

const BLOOD_GROUPS = ["A+", "A-", "B+", "B-", "O+", "O-", "AB+", "AB-"];
// 01XXXXXXXXX, optionally with +88 / 88 in front; spaces and dashes are fine.
const BD_MOBILE = /^(\+?88)?01\d{9}$/;
const isMobile = (value) => BD_MOBILE.test(String(value || "").replace(/[\s-]/g, ""));
const mobileRule = {
  validator: (_, v) => (!v || isMobile(v) ? Promise.resolve() : Promise.reject(new Error("Use a number like 01700000000"))),
};

// YYYY-MM-DD in local time, for <input type="date">.
const toDateInput = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formValuesFor = (donor) =>
  donor
    ? {
        name: donor.name || "",
        phone: donor.phone || "",
        blood: donor.blood ? String(donor.blood).toUpperCase() : undefined,
        district: donor.district || undefined,
        // The mobile app saves "Savar Upazila"; the list uses "Savar".
        upuzila: donor.upuzila ? String(donor.upuzila).replace(/\s*Upazila\s*$/i, "").trim() : undefined,
        mobile1: donor.mobile1 || "",
        email: donor.email || "",
        lastDonation: toDateInput(donor.lastDonation),
        is_contact_public: donor.is_contact_public !== false,
      }
    : { is_contact_public: true };

// Add a donor (no `donor`) or edit one (`donor` is the row being edited).
const DonorFormModal = ({ open, donor, onClose }) => {
  const editing = !!donor;
  const queryClient = useQueryClient();
  const { createDonor, updateDonor } = useAdmin();
  const { districts, getUpazilas } = useData();
  const [form] = Form.useForm();
  const [addAnother, setAddAnother] = useState(false);
  const district = Form.useWatch("district", form);
  const initialValues = useMemo(() => formValuesFor(donor), [donor]);
  const today = toDateInput(new Date());

  // Start from this donor's details (or a blank form) every time it opens.
  useEffect(() => {
    if (open) form.resetFields();
  }, [open, donor, form]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["admin-donors"] });
    queryClient.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const { mutate: save, isPending } = useMutation({
    mutationFn: (values) => (editing ? updateDonor({ id: donor._id, ...values }) : createDonor(values)),
    onSuccess: (res) => {
      refresh();
      if (editing) {
        toast.success(res.passwordChanged ? `${res.user.name} updated, with a new password.` : `${res.user.name} updated.`);
        onClose();
        return;
      }
      toast.success(
        res.hasPassword
          ? `${res.user.name} added.`
          : `${res.user.name} added. Their password is their mobile number (${res.user.phone}) until they change it.`
      );
      form.resetFields();
      if (!addAnother) onClose();
    },
    onError: (err) => toast.error(serverErrorMessage(err, editing ? "Could not save the changes" : "Could not add the donor")),
  });

  const submit = (values) => {
    save({
      ...values,
      lastDonation: values.lastDonation ? new Date(values.lastDonation).toISOString() : null,
    });
  };

  const submitWith = (another) => {
    setAddAnother(another);
    form.submit();
  };

  const footer = editing
    ? [
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={isPending} onClick={() => submitWith(false)}>
          Save changes
        </Button>,
      ]
    : [
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="another" loading={isPending && addAnother} onClick={() => submitWith(true)}>
          Save &amp; add another
        </Button>,
        <Button key="save" type="primary" loading={isPending && !addAnother} onClick={() => submitWith(false)}>
          Save donor
        </Button>,
      ];

  return (
    <Modal
      className="adm-overlay"
      open={open}
      title={editing ? `Edit donor · ${donor.name || "Unnamed"}` : "Add a donor"}
      onCancel={onClose}
      width={560}
      footer={footer}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={submit}
        className="add-donor"
        requiredMark="optional"
      >
        <div className="add-donor__grid">
          <Form.Item
            label="Full name"
            name="name"
            rules={[{ required: true, whitespace: true, message: "Enter the donor's name" }]}
            className="add-donor__full"
          >
            <Input placeholder="e.g. Rahim Uddin" autoFocus maxLength={100} />
          </Form.Item>

          <Form.Item
            label="Mobile number"
            name="phone"
            rules={[{ required: true, message: "Enter a mobile number" }, mobileRule]}
            extra={editing ? "This is also their login." : undefined}
          >
            <Input inputMode="tel" placeholder="01700000000" />
          </Form.Item>

          <Form.Item label="Blood group" name="blood" rules={[{ required: true, message: "Pick a blood group" }]}>
            <Select placeholder="Select" options={BLOOD_GROUPS.map((g) => ({ label: g, value: g }))} />
          </Form.Item>

          <Form.Item label="District" name="district">
            <Select
              showSearch
              allowClear
              placeholder="Select district"
              optionFilterProp="label"
              options={districts.map((d) => ({ label: d.name, value: d.name }))}
              onChange={() => form.setFieldsValue({ upuzila: undefined })}
            />
          </Form.Item>

          <Form.Item label="Upazila" name="upuzila">
            <Select
              showSearch
              allowClear
              disabled={!district}
              placeholder={district ? "Select upazila" : "Pick a district first"}
              optionFilterProp="label"
              options={getUpazilas(district).map((u) => ({ label: u, value: u }))}
            />
          </Form.Item>

          <Form.Item label="Alternate number" name="mobile1" rules={[mobileRule]}>
            <Input inputMode="tel" placeholder="Optional" />
          </Form.Item>

          <Form.Item label="Email" name="email" rules={[{ type: "email", message: "That email doesn't look right" }]}>
            <Input inputMode="email" placeholder="Optional" />
          </Form.Item>

          <Form.Item
            label="Last donation"
            name="lastDonation"
            extra={editing ? "Clear it if they haven't donated." : "Leave empty if they haven't donated yet."}
            rules={[
              {
                validator: (_, v) =>
                  !v || v <= today ? Promise.resolve() : Promise.reject(new Error("Can't be in the future")),
              },
            ]}
          >
            <Input type="date" max={today} />
          </Form.Item>

          <Form.Item
            label={editing ? "New password" : "Password"}
            name="password"
            rules={[{ min: 6, message: "At least 6 characters" }]}
            extra={
              editing
                ? "Leave empty to keep their current password."
                : "Optional. If empty, their mobile number is the password until they change it."
            }
          >
            <Input.Password autoComplete="new-password" placeholder="Optional" />
          </Form.Item>

          <Form.Item
            label="Show phone number to members"
            name="is_contact_public"
            valuePropName="checked"
            className="add-donor__full"
          >
            <Switch />
          </Form.Item>

          {editing ? null : (
            <Form.Item label="Admin note" name="note" className="add-donor__full" extra="Only admins can see this.">
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 5 }}
                maxLength={2000}
                placeholder="Optional, e.g. Met at the October blood camp"
              />
            </Form.Item>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default DonorFormModal;
